// User-owned creator accounts. No generation key or platform-funded fallback exists here.
export const CREATOR_PROVIDERS = ['higgsfield', 'buffer', 'windsor'] as const;
export type CreatorProvider = typeof CREATOR_PROVIDERS[number];
export type Env = (key: string) => string | undefined;
export class CreatorError extends Error {
  status: number;
  constructor(code: string, status = 400) { super(code); this.status = status; }
}
export const creatorConfig = {
  higgsfield: {
    // Canonical server advertised by Higgsfield's protected-resource metadata (2026-09-30).
    // Keep issuer, authorization and token endpoints on the same discovered authority.
    issuer: 'https://clerk.higgsfield.ai', authorize: 'https://clerk.higgsfield.ai/oauth/authorize',
    token: 'https://clerk.higgsfield.ai/oauth/token', registration: 'https://clerk.higgsfield.ai/oauth/register',
    userinfo: 'https://clerk.higgsfield.ai/oauth/userinfo',
    resource: 'https://mcp.higgsfield.ai/mcp', scopes: ['openid', 'email', 'offline_access'],
    client: 'CREATOR_HIGGSFIELD_CLIENT_ID',
  },
  buffer: {
    issuer: 'https://auth.buffer.com', authorize: 'https://auth.buffer.com/auth',
    token: 'https://auth.buffer.com/token', registration: null, resource: 'https://api.buffer.com',
    userinfo: null,
    scopes: ['account:read', 'posts:read', 'offline_access'], client: 'CREATOR_BUFFER_CLIENT_ID',
  },
  windsor: {
    issuer: 'https://mcp.windsor.ai/', authorize: 'https://mcp.windsor.ai/authorize',
    token: 'https://mcp.windsor.ai/token', registration: 'https://mcp.windsor.ai/register',
    // The MCP transport itself challenges for create/delete, including read requests.
    // The runtime still exposes only explicitly allowlisted reads; user consent is required.
    resource: 'https://mcp.windsor.ai/', scopes: ['create', 'delete', 'offline_access'],
    userinfo: null, client: 'CREATOR_WINDSOR_CLIENT_ID',
  },
} as const;
export function isCreatorProvider(value: unknown): value is CreatorProvider {
  return typeof value === 'string' && (CREATOR_PROVIDERS as readonly string[]).includes(value);
}
export function creatorAllowed(owner: string, env: Env, provider?: CreatorProvider) {
  if (!owner?.trim() || env('CREATOR_CONNECTIONS_ENABLED') !== 'true') return false;
  const cohort = (env('CREATOR_ALLOWED_USER_IDS') || '').split(',').map(s => s.trim()).filter(Boolean).includes(owner);
  if (cohort) return true;
  if (env('CREATOR_PUBLIC_ENABLED') !== 'true') return false;
  // Public eligibility must always name its provider; generic legacy callers
  // retain cohort-only admission and cannot accidentally open Buffer.
  // Buffer retains its validation cohort until channel/publication acceptance.
  if (provider !== 'higgsfield' && provider !== 'windsor') return false;
  const client = env(creatorConfig[provider].client);
  return !!client && client.length <= 2000;
}
export function creatorClient(provider: CreatorProvider, env: Env) {
  const id = env(creatorConfig[provider].client);
  if (!id || id.length > 2000) throw new CreatorError('provider_not_configured', 503);
  return id;
}
export const randomProof = () => base64url(crypto.getRandomValues(new Uint8Array(32)));
export function base64url(bytes: Uint8Array) {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export async function proofHash(value: string) {
  return base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))));
}
export async function creatorAuthorize(provider: CreatorProvider, client: string, callback: string, state: string, verifier: string, write = false) {
  const config = creatorConfig[provider];
  const params = new URLSearchParams({client_id: client, redirect_uri: callback, response_type: 'code',
    scope: [...config.scopes,...(provider==='buffer' && write ? ['posts:write'] : [])].join(' '), state, code_challenge: await proofHash(verifier), code_challenge_method: 'S256',
    response_mode: 'query'});
  if (provider !== 'buffer') params.set('resource', config.resource);
  // Buffer documents explicit consent when obtaining offline access.
  if (provider === 'buffer') params.set('prompt', 'consent');
  return config.authorize + '?' + params;
}
export function checkCallbackIssuer(provider: CreatorProvider, issuer: string | null) {
  // Clerk and Windsor advertise RFC 9207; missing or non-exact issuers must fail closed.
  if ((provider !== 'buffer' || issuer !== null) && issuer !== creatorConfig[provider].issuer) {
    throw new CreatorError('issuer_mismatch', 403);
  }
}
export function normalizeToken(data: Record<string, unknown>, requested: readonly string[]) {
  // Only field names/types are logged; never credential values or provider payloads.
  const invalid: string[] = [];
  if (data.error) invalid.push('error');
  if (typeof data.access_token !== 'string' || !data.access_token || data.access_token.length > 32000) invalid.push('access_token');
  if (typeof data.token_type !== 'string' || data.token_type.toLowerCase() !== 'bearer') invalid.push('token_type');
  if (typeof data.expires_in !== 'number' || !Number.isFinite(data.expires_in) || data.expires_in <= 0 || data.expires_in > 31536000) invalid.push('expires_in');
  if (typeof data.refresh_token !== 'string' || !data.refresh_token || data.refresh_token.length > 32000) invalid.push('refresh_token');
  if (invalid.length) {
    console.warn('creator_token_invalid_fields', invalid.join(','));
    throw new CreatorError('invalid_token_response', 502);
  }
  if (data.scope !== undefined && typeof data.scope !== 'string') throw new CreatorError('invalid_token_response', 502);
  const scopes = typeof data.scope === 'string' ? data.scope.split(/\s+/).filter(Boolean) : [...requested];
  const missing = requested.filter(scope => !scopes.includes(scope));
  if (missing.length) {
    // Log only our requested scope names, never the provider's raw response.
    console.warn('creator_token_missing_scopes', missing.join(','));
    throw new CreatorError('permissions_incomplete', 403);
  }
  return {access: data.access_token as string, refresh: data.refresh_token as string, expiresIn: data.expires_in as number, scopes};
}
export function safeText(value: unknown, max = 200): string {
  return typeof value === 'string' ? value.slice(0, max) : '';
}
